import axios from "axios";
import React, { useEffect, useState } from "react";
import { api } from "../utils/Api";
import { Typography, Flex, Button, notification, Tag, Statistic } from "antd";
import Layout from "../layout/Layout";
import {
	CustomSearch,
	CustomSpinner,
	CustomTable,
} from "../utils/CustomComponents";
import dayjs from "dayjs";
import { RetweetOutlined, RollbackOutlined } from "@ant-design/icons";
import QAQCJobLoadsInternalModal from "./QAQCJobLoadsInternalModal";
import QAQCJobLoadsExternalModal from "./QAQCJobLoadsExternalModal";
const { Text } = Typography;

const QAQCCylinderTestingReport = ({ history }) => {
	const [loading, setLoading] = useState(true);
	const [loadingTable, setLoadingTable] = useState(false);
	const [jobList, setJobList] = useState([]);
	const [allJobs, setAllJobs] = useState([]);
	const [startDate, setStartDate] = useState(dayjs());
	const [endDate, setEndDate] = useState(dayjs());
	const [apiNotification, contextHolder] = notification.useNotification();

	const [refresh, setRefresh] = useState(false);

	const [modalOpenLoadsInternal, setModalOpenLoadsInternal] = useState(false);
	const [modalOpenLoadsExternal, setModalOpenLoadsExternal] = useState(false);
	const [hasLaboratoryLoads, setHasLaboratoryLoads] = useState(true);
	const [selectedJob, setSelectedJob] = useState(null);

	const [customer, setCustomer] = useState("All");
	const [customerList, setCustomerList] = useState([]);
	const [customerLoading, setCustomerLoading] = useState(true);

	const [jobId, setJobId] = useState("All");
	const [jobIdList, setJobIdList] = useState([]);
	const [jobLoading, setJobLoading] = useState(true);

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	/**
	 ** Options header (columns)
	 */
	const columns = [
		{
			title: "Date",
			dataIndex: "scheduledDate",
			key: "scheduledDate",
			sorter: (a, b) =>
				dayjs(a.scheduledDate).valueOf() - dayjs(b.scheduledDate).valueOf(),
			render: (value) => {
				if (!value || !dayjs(value).isValid()) return "-";
				return <Text>{dayjs(value).format("MM/DD/YYYY hh:mm A")}</Text>;
			},
		},
		{
			title: "Job ID",
			dataIndex: "number",
			key: "number",
			sorter: (a, b) => Number(a.number) - Number(b.number),
			render: (value, record) => {
				return (
					<Text
						strong
						underline
						style={{ cursor: "pointer" }}
						onClick={() => {
							setSelectedJob(record);
							if (record.qcType === "Internal") {
								setModalOpenLoadsInternal(true);
							} else {
								setModalOpenLoadsExternal(true);
							}
						}}
					>
						{value}
					</Text>
				);
			},
		},
		{
			title: "Customer",
			dataIndex: "customer",
			key: "customer",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Plant",
			dataIndex: "plant",
			key: "plant",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Address",
			dataIndex: "address",
			key: "address",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Seller",
			dataIndex: "seller",
			key: "seller",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Yards",
			dataIndex: "yards",
			key: "yards",
			render: (value) => {
				return (
					<Statistic
						value={value}
						valueStyle={{
							fontSize: 14,
						}}
					/>
				);
			},
		},
		{
			title: "QC Type",
			dataIndex: "qcType",
			key: "qcType",
			render: (value) => {
				return (
					<Tag color={value === "Internal" ? "purple" : "yellow"}>{value}</Tag>
				);
			},
		},
		{
			title: "Franchise",
			dataIndex: "franchise",
			key: "franchise",
			render: (value) => {
				return (
					<Tag
						color={
							value === "57Concrete"
								? "blue"
								: value === "Castle"
									? "green"
									: "cyan"
						}
					>
						{value}
					</Tag>
				);
			},
		},
	];

	useEffect(() => {
		getJobList();
		getJobIDList();
	}, [startDate, endDate, customer, hasLaboratoryLoads, refresh]);

	const getJobList = async () => {
		try {
			setLoadingTable(true);

			const start = `${startDate.year()}-${
				startDate.month() + 1
			}-${startDate.date()}`;
			const end = `${endDate.year()}-${endDate.month() + 1}-${endDate.date()}`;

			const url =
				api +
				`/job/get/qc/laboratory/all?startDate=${start}&endDate=${end}&hasLaboratoryLoads=${hasLaboratoryLoads}&customer=${customer}`;

			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.get(url);
			if (data.success) {
				let list = [];
				data.payload.map((item, index) => {
					list.push({
						key: index,
						id: item._id,
						number: item.number,
						qcType: item.qcType ?? "-",
						franchise: item.franchise || "57Concrete",
						customer: item.customer.name,
						seller: item.seller.name,
						scheduledDate: item.scheduledDate,
						address: `${item.customerShipAddress} - (${item.shipAddress})`,
						yards: item.loadYD3,
						plant: item.plant.name,
					});
				});
				setAllJobs(list);
				setJobList(list);
			} else {
				openNotification("error", "Error", data.message);
			}
			setLoadingTable(false);
			setLoading(false);
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", err.message);
			return null;
		}
	};

	const getJobIDList = async () => {
		try {
			setJobLoading(true);

			const start = `${startDate.year()}-${
				startDate.month() + 1
			}-${startDate.date()}`;
			const end = `${endDate.year()}-${endDate.month() + 1}-${endDate.date()}`;

			const url =
				api +
				`/job/get/qc/laboratory/ids/all?startDate=${start}&endDate=${end}&hasLaboratoryLoads=${hasLaboratoryLoads}&customer=${customer}`;

			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.get(url);
			if (data.success) {
				let list = [
					{
						value: "All",
						label: "All",
					},
				];
				data.payload.map((item) => {
					list.push({
						label: item.number.toString(),
						value: item._id,
					});
				});
				setJobIdList(list);
			} else {
				openNotification("error", "Error", data.message);
			}
			setJobLoading(false);
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", err.message);
			return null;
		}
	};

	useEffect(() => {
		if (jobId === "All") {
			setJobList(allJobs);
		} else {
			const filtered = allJobs.filter((job) => job.id === jobId);
			setJobList(filtered);
		}
	}, [jobId, allJobs]);

	useEffect(() => {
		setJobId("All");
	}, [customer, hasLaboratoryLoads]);

	useEffect(() => {
		getCustomers();
	}, []);

	const getCustomers = async () => {
		try {
			const url = api + "/customer/list/options/57Concrete";
			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.get(url);
			if (data.success) {
				let list = [
					{
						value: "All",
						label: "All",
					},
				];
				data.payload.map((item) => {
					list.push({
						label: `${item.customerNumber} ${item.name}`,
						value: item._id,
					});
				});
				setCustomerList(list);
			} else {
				openNotification("error", "Error", data.message);
			}
			setCustomerLoading(false);
		} catch (err) {
			setCustomerLoading(false);
			openNotification("error", "Error", "Error while trying to get customers");
			console.error(err.message);
			return null;
		}
	};

	const RenderTable = () => {
		return (
			<CustomTable columns={columns} data={jobList} loading={loadingTable} />
		);
	};

	const onRangeChange = (dates) => {
		if (dates) {
			setStartDate(dates[0]);
			setEndDate(dates[1]);
		}
	};

	const RenderSearch = () => (
		<Flex gap={10} align="end" justify="space-between">
			<CustomSearch
				loading={loading}
				startDate={startDate}
				endDate={endDate}
				onRangeChange={onRangeChange}
				dateFormat={"MM/DD/YYYY"}
				pickerValue={hasLaboratoryLoads}
				pickerWidth={300}
				labelPicker={"Filter By Testing Loads"}
				onChangePicker={(value) => setHasLaboratoryLoads(value)}
				allowClearPicker={false}
				optionsPicker={[
					{
						value: true,
						label: "With Laboratory Testing Loads",
					},
					{
						value: false,
						label: "Without Laboratory Testing Loads",
					},
				]}
				labelPicker2={"Select customer"}
				pickerValue2={customer}
				optionsPicker2={customerList}
				onChangePicker2={(v) => setCustomer(v)}
				allowClearPicker2={false}
				pickerWidth2={250}
				disabledPicker={customerLoading}
				labelPicker3={"Select Job ID"}
				pickerValue3={jobId}
				optionsPicker3={jobIdList}
				onChangePicker3={(v) => setJobId(v)}
				allowClearPicker3={false}
				pickerWidth3={250}
				disabledPicker3={jobLoading}
				pickerTitle={"Has Testing Loads?"}
				pickerTitle2={"Customer"}
				pickerTitle3={"Job ID"}
			/>
			<Button
				type="primary"
				onClick={() => setRefresh((prev) => !prev)}
				icon={<RetweetOutlined />}
				size="large"
				loading={loading}
			/>
		</Flex>
	);

	const goBack = () => {
		history.goBack();
	};

	return (
		<Layout title="QA/QC Cylinder Testing Dashboard" description="Dashboard">
			<Flex vertical gap={16}>
				<Button
					type="primary"
					size="large"
					icon={<RollbackOutlined />}
					onClick={goBack}
					loading={loading}
					disabled={loading}
				/>
				<RenderSearch />
				{loading ? <CustomSpinner /> : <RenderTable />}
				{contextHolder}
				<QAQCJobLoadsInternalModal
					modalOpenLoads={modalOpenLoadsInternal}
					setModalOpenLoads={setModalOpenLoadsInternal}
					selectedJob={selectedJob}
					setSelectedJob={setSelectedJob}
					history={history}
				/>
				<QAQCJobLoadsExternalModal
					modalOpenLoads={modalOpenLoadsExternal}
					setModalOpenLoads={setModalOpenLoadsExternal}
					selectedJob={selectedJob}
					setSelectedJob={setSelectedJob}
				/>
			</Flex>
		</Layout>
	);
};

export default QAQCCylinderTestingReport;
